import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

export default {
  async getLoadtypeById(loadtypeId, options) {
    const response = await axios.get(`loadtype/${loadtypeId}`);
    return getResponse(response, options);
  },
  /**
   * Get load type by ID with improved API contract.
   *
   * @param {string} loadtypeId - Load type ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<unknown>} The response data
   */
  async getLoadtypeByIdV2(
    loadtypeId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `loadtype/${loadtypeId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  async getLoadtypes(params, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`loadtype?${queryString}`, {
      params
    });
    return getResponse(response, options);
  },
  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * List load types with improved API contract.
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getLoadtypesV2(options) {
    const { getResponseConfig, requestConfig, query, enableCache } = {
      getResponseConfig: {},
      requestConfig: {},
      query: {},
      enableCache: false,
      ...options
    };
    const queryString = getQueryStringFromOptions(query);
    const url = `loadtype${queryString ? `?${queryString}` : ''}`;

    const response = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });
    return getResponse(response, getResponseConfig);
  },
  /**
   * Create a load type.
   *
   * @template TResponse
   * @param {Record<string, unknown>} data - Load type create payload.
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options] - Response handling options.
   * @returns {Promise<TResponse>}
   */
  async createLoadtype(data, options = {}) {
    const response = await axios.post('loadtype', data);
    return getResponse(response, options);
  },
  async getAvailability(id, params, options) {
    const response = await axios.post(`loadtype/${id}/get-availability`, params);
    return getResponse(response, options);
  }
};
